import { createBrowserRouter, Navigate } from "react-router-dom";

import { RouteErrorPage } from "@/app/RouteErrorPage";
import { AppShell } from "@/components/shell/AppShell";

const lazyEmbedBidFileReview = () =>
  import("@/pages/embed/EmbedBidFileReviewPage").then(
    ({ EmbedBidFileReviewPage: Component }) => ({ Component }),
  );
const lazyDashboard = () =>
  import("@/pages/aggregate/DashboardPage").then(
    ({ DashboardPage: Component }) => ({ Component }),
  );
const lazyProjectsList = () =>
  import("@/pages/aggregate/ProjectsListPage").then(
    ({ ProjectsListPage: Component }) => ({ Component }),
  );
const lazyReportsList = () =>
  import("@/pages/aggregate/ReportsListPage").then(
    ({ ReportsListPage: Component }) => ({ Component }),
  );
const lazyReportDetail = () =>
  import("@/pages/aggregate/ReportDetailPage").then(
    ({ ReportDetailPage: Component }) => ({ Component }),
  );
const lazyAnalysisRuns = () =>
  import("@/pages/analysis/AnalysisRunsPage").then(
    ({ AnalysisRunsPage: Component }) => ({ Component }),
  );
const lazyAnalysisRunResult = () =>
  import("@/pages/analysis/AnalysisRunResultPage").then(
    ({ AnalysisRunResultPage: Component }) => ({ Component }),
  );
const lazySettingsApiKey = () =>
  import("@/pages/settings/SettingsApiKeyPage").then(
    ({ SettingsApiKeyPage: Component }) => ({ Component }),
  );
const lazySettingsRules = () =>
  import("@/pages/settings/SettingsRulesPage").then(
    ({ SettingsRulesPage: Component }) => ({ Component }),
  );
const lazySettingsTroubleshoot = () =>
  import("@/pages/settings/SettingsTroubleshootPage").then(
    ({ SettingsTroubleshootPage: Component }) => ({ Component }),
  );
const lazyLicenseOcr = () =>
  import("@/pages/abilities/LicenseOcr").then(
    ({ LicenseOcrPage: Component }) => ({ Component }),
  );
const lazyBidderRisk = () =>
  import("@/pages/abilities/BidderRisk").then(
    ({ BidderRiskPage: Component }) => ({ Component }),
  );
const lazyTenderRisk = () =>
  import("@/pages/abilities/TenderRisk").then(
    ({ TenderRiskPage: Component }) => ({ Component }),
  );
const lazyDirectAwardReview = () =>
  import("@/pages/abilities/DirectAwardReview").then(
    ({ DirectAwardReviewPage: Component }) => ({ Component }),
  );
const lazyBidReview = () =>
  import("@/pages/abilities/BidReview").then(
    ({ BidReviewPage: Component }) => ({ Component }),
  );
const lazyContractExtract = () =>
  import("@/pages/abilities/ContractExtract").then(
    ({ ContractExtractPage: Component }) => ({ Component }),
  );
const lazyAwardExtract = () =>
  import("@/pages/abilities/AwardExtract").then(
    ({ AwardExtractPage: Component }) => ({ Component }),
  );
const lazyScanDocxConsistency = () =>
  import("@/pages/abilities/ScanDocxConsistency").then(
    ({ ScanDocxConsistencyPage: Component }) => ({ Component }),
  );
const lazyQualificationOcr = () =>
  import("@/pages/abilities/QualificationOcr").then(
    ({ QualificationOcrPage: Component }) => ({ Component }),
  );
const lazyInvoiceOcr = () =>
  import("@/pages/abilities/InvoiceOcr").then(
    ({ InvoiceOcrPage: Component }) => ({ Component }),
  );
const lazyMaterialPreReview = () =>
  import("@/pages/abilities/MaterialPreReview").then(
    ({ MaterialPreReviewPage: Component }) => ({ Component }),
  );
const lazyContractTeamConsistency = () =>
  import("@/pages/abilities/ContractTeamConsistency").then(
    ({ ContractTeamConsistencyPage: Component }) => ({ Component }),
  );
const lazySupplierRelationship = () =>
  import("@/pages/abilities/SupplierRelationship").then(
    ({ SupplierRelationshipPage: Component }) => ({ Component }),
  );
const lazyContractCompliance = () =>
  import("@/pages/abilities/ContractCompliance").then(
    ({ ContractCompliancePage: Component }) => ({ Component }),
  );

export const router = createBrowserRouter([
  // 主平台嵌入页 — 独立于 AppShell (无导航/设置), 会话经跳转凭证兑换。
  {
    path: "/embed/bid-file-review",
    lazy: lazyEmbedBidFileReview,
    errorElement: <RouteErrorPage />,
  },
  {
    path: "/",
    element: <AppShell />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, lazy: lazyDashboard },

      // 项目入口: 只说明项目维度汇总要等 ERP 字段合同, 并给真实入口
      { path: "projects", lazy: lazyProjectsList },

      // 项目卷宗 / 审计案卷 / 趋势看板 / 历史档案室 四个页面已删 (2026-07-30):
      // 通篇是 "—" 占位、零 API、导航不收、代码无人链接 —— 与 /files 同一形态, 主数据
      // 在 ERP 侧, 平台没有对应实体表, 不存在"接上数据"的将来。旧 URL 收敛到分析记录,
      // 而不是留一个看起来像功能的空页。
      {
        path: "projects/:projectId",
        element: <Navigate to="/analysis-runs" replace />,
      },
      { path: "audit", element: <Navigate to="/analysis-runs" replace /> },
      {
        path: "audit/:caseId",
        element: <Navigate to="/analysis-runs" replace />,
      },
      { path: "insights", element: <Navigate to="/analysis-runs" replace /> },
      { path: "archives", element: <Navigate to="/analysis-runs" replace /> },

      // 报告中心
      { path: "reports", lazy: lazyReportsList },
      { path: "reports/:reportId", lazy: lazyReportDetail },

      // 分析记录 / 分析结果
      { path: "analysis-runs", lazy: lazyAnalysisRuns },
      { path: "analysis-runs/:useCaseId/:runId", lazy: lazyAnalysisRunResult },

      // 设置
      { path: "settings", element: <Navigate to="/settings/api" replace /> },
      { path: "settings/api", lazy: lazySettingsApiKey },
      { path: "settings/rules", lazy: lazySettingsRules },
      { path: "settings/troubleshoot", lazy: lazySettingsTroubleshoot },

      // Atomic abilities — 每个 UC 一页
      { path: "abilities/company-profile", lazy: lazyLicenseOcr },
      { path: "abilities/qualification-extract", lazy: lazyQualificationOcr },
      { path: "abilities/invoice-ocr", lazy: lazyInvoiceOcr },
      { path: "abilities/company-risk", lazy: lazyBidderRisk },
      { path: "abilities/pre-bid-review", lazy: lazyMaterialPreReview },
      { path: "abilities/bid-file-review", lazy: lazyBidReview },
      { path: "abilities/award-extract", lazy: lazyAwardExtract },
      { path: "abilities/tender-compliance", lazy: lazyTenderRisk },
      { path: "abilities/direct-award-review", lazy: lazyDirectAwardReview },
      { path: "abilities/contract-extract", lazy: lazyContractExtract },
      { path: "abilities/contract-consistency", lazy: lazyScanDocxConsistency },
      {
        path: "abilities/personnel-consistency",
        lazy: lazyContractTeamConsistency,
      },
      { path: "abilities/contract-compliance", lazy: lazyContractCompliance },
      {
        path: "abilities/supplier-relationship",
        lazy: lazySupplierRelationship,
      },

      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
